import { useCallback, useSyncExternalStore } from "react";

const MOBILE_BREAKPOINT = 640;
const TABLET_BREAKPOINT = 1024;

type WidthSubscriber = () => void;
type HeightSubscriber = () => void;

let currentWidth: number | null =
  typeof window === "undefined" ? null : window.innerWidth;
let currentHeight: number | null =
  typeof window === "undefined" ? null : window.innerHeight;

const widthSubscribers = new Set<WidthSubscriber>();
const heightSubscribers = new Set<HeightSubscriber>();

const notifyWidthSubscribers = () => {
  widthSubscribers.forEach((callback) => callback());
};

const notifyHeightSubscribers = () => {
  heightSubscribers.forEach((callback) => callback());
};

const syncWindowWidth = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const nextWidth = window.innerWidth;
  const widthChanged = currentWidth !== nextWidth;
  currentWidth = nextWidth;

  return widthChanged;
};

const syncWindowHeight = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const nextHeight = window.innerHeight;
  const heightChanged = currentHeight !== nextHeight;
  currentHeight = nextHeight;

  return heightChanged;
};

const handleResize = () => {
  syncWindowWidth();
  syncWindowHeight();
  notifyWidthSubscribers();
  notifyHeightSubscribers();
};

let resizeListenerCount = 0;

function addResizeListener() {
  if (resizeListenerCount === 0) {
    window.addEventListener("resize", handleResize);
  }
  resizeListenerCount++;
}

function removeResizeListener() {
  resizeListenerCount--;
  if (resizeListenerCount === 0) {
    window.removeEventListener("resize", handleResize);
  }
}

function subscribeWidth(callback: WidthSubscriber) {
  if (typeof window === "undefined") {
    return () => {};
  }
  widthSubscribers.add(callback);
  addResizeListener();
  if (syncWindowWidth()) {
    notifyWidthSubscribers();
  }

  return () => {
    widthSubscribers.delete(callback);
    removeResizeListener();
  };
}

function subscribeHeight(callback: HeightSubscriber) {
  if (typeof window === "undefined") {
    return () => {};
  }
  heightSubscribers.add(callback);
  addResizeListener();
  if (syncWindowHeight()) {
    notifyHeightSubscribers();
  }

  return () => {
    heightSubscribers.delete(callback);
    removeResizeListener();
  };
}

export function useWindowWidth() {
  const getSnapshot = useCallback(() => currentWidth, []);
  const getServerSnapshot = useCallback(() => null, []);

  return useSyncExternalStore(subscribeWidth, getSnapshot, getServerSnapshot);
}

export function useWindowHeight() {
  const getSnapshot = useCallback(() => currentHeight, []);
  const getServerSnapshot = useCallback(() => null, []);

  return useSyncExternalStore(subscribeHeight, getSnapshot, getServerSnapshot);
}

export function useIsMobile() {
  const width = useWindowWidth();
  return width !== null && width < MOBILE_BREAKPOINT;
}

export function useIsTablet() {
  const width = useWindowWidth();
  return (
    width !== null && width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT
  );
}
